import type { LabeledTextGridComponent } from "../../types";

const TITLE_COLOR = "#0095DB";

// Classes LITTÉRALES par point de rupture. Une classe construite par gabarit,
// `md:${COLS[n]}`, n'est pas vue par le scanner Tailwind et n'existe tout
// simplement pas dans le CSS produit : la grille retombait alors sur deux
// colonnes quel que soit le réglage de l'éditeur.
const MD_COLS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};
const LG_COLS: Record<number, string> = {
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
};

function toPerRow(v: unknown): 2 | 3 | 4 {
  const n = Number(v);
  return n >= 2 && n <= 4 ? (n as 2 | 3 | 4) : 2;
}

function Card({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="flex h-full flex-col text-left">
      <h3 className="text-base xl:text-lg font-semibold md:text-[17px]" style={{ color: TITLE_COLOR }}>
        {title}
      </h3>
      <div className="mt-2 space-y-1 text-sm xl:text-base md:text-base text-black">
        {lines.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </div>
  );
}

/**
 * Grille de rubriques titrées.
 *
 * UNE SEULE grille, à toutes les largeurs. Le rendu précédent sortait la dernière
 * rangée incomplète dans une seconde grille centrée à une largeur fractionnaire
 * (`maxWidth: 66%`, marges auto) : ses colonnes ne tombaient jamais en face de
 * celles du dessus, et comme le style était en ligne il s'appliquait aussi sur
 * téléphone. Cinq rubriques donnaient l'escalier que la Banque a relevé.
 *
 * Le champ `centerLastRow` reste accepté, les contenus existants le portent, mais
 * il ne découpe plus la grille : une rangée incomplète se cale à gauche, dans le
 * même rythme de colonnes que les rangées pleines. Une colonne alignée vaut mieux
 * qu'une rangée centrée de travers.
 */
export function LabeledTextGridRenderer({ component }: { component: LabeledTextGridComponent }) {
  const { items, cardsPerRowTablet, cardsPerRowDesktop } = component.content;
  const tablet = toPerRow(cardsPerRowTablet);
  const desktop = toPerRow(cardsPerRowDesktop);
  // Téléphone : une colonne pleine largeur. Deux colonnes sous 640 px coupaient
  // les libellés longs (« Les imprimés du domaine bancaire ») en trois lignes.
  const gridCols = `grid-cols-1 sm:grid-cols-2 ${MD_COLS[tablet]} ${LG_COLS[desktop]}`;

  return (
    <section className="w-full">
      {/* `items-start` et non `justify-items-start` : les cellules gardent toute la
          largeur de leur colonne (donc des colonnes égales), seul le contenu se cale
          en haut. L'ancien `justify-items-start` dimensionnait chaque carte à son
          texte, d'où des largeurs différentes d'une rangée à l'autre. */}
      <div className={`grid w-full items-start gap-6 md:gap-8 ${gridCols}`}>
        {items.map((item, i) => (
          <Card key={`${item.title}-${i}`} title={item.title} lines={item.lines ?? []} />
        ))}
      </div>
    </section>
  );
}
